import React, { useCallback, useMemo } from 'react';

import { FormattedMessage, useIntl } from 'react-intl';
import { di } from 'react-magnetic-di';

import { cssMap } from '@atlaskit/css';
import UploadIcon from '@atlaskit/icon/core/cloud-arrow-up';
import TrashIcon from '@atlaskit/icon/core/delete';
import Link from '@atlaskit/link/link';
import type { UploadsStartEventPayload } from '@atlaskit/media-picker/types';
import { fg } from '@atlaskit/platform-feature-flags/fg';
import { Box } from '@atlaskit/primitives/compiled';
import { token } from '@atlaskit/tokens';

import type { FilesValue } from '../../../../../../../../common/types/form/form-fields/field-values';
import { asFilesValue } from '../../../../../../../../common/types/form/form-fields/field-values/utils';
import type { FormFieldComponentRendererProps } from '../../../../../../../../common/types/renderers';
import { AttachmentErrorState } from '../../../../../../../../common/ui/attachment-error-state';
import { AttachmentSharedFilmstrip } from '../../../../../../../../common/ui/shared-filmstrip';
import {
	useAttachmentConfiguration,
	useCurrentValue,
	useFieldInvalid,
	useFormField,
} from '../../../../../../../../controllers/form-renderer-store';
import { AttachmentPicker } from './attachment-picker';
import { messages } from './messages';
import { getDropzone } from './utils/get-dropzone';
import { useIsDragActive } from './utils/is-drag-active';
import { useOnRemoveFile } from './utils/on-remove-file';
import { useOnUploadEnd } from './utils/on-upload-end';
import { useOnUploadError } from './utils/on-upload-error';
import { useOnUploadStart } from './utils/on-upload-start';

const styles = cssMap({
	container: {
		display: 'flex',
		flexDirection: 'column',
		justifyContent: 'center',
		paddingTop: token('space.300'),
		paddingBottom: token('space.300'),
		borderWidth: token('border.width'),
		borderStyle: 'dashed',
		color: token('color.text.subtlest'),
	},
	dropzoneContainer: {
		display: 'flex',
		alignItems: 'center',
		justifyContent: 'center',
		paddingTop: token('space.150'),
		paddingRight: token('space.150'),
		paddingBottom: token('space.150'),
		paddingLeft: token('space.150'),
	},
});

const EMPTY_FILES_VALUE: FilesValue = { files: [] };

export const EditAttachmentField = ({
	fieldId,
	fieldErrorId,
}: FormFieldComponentRendererProps): React.JSX.Element => {
	di(useAttachmentConfiguration, useCurrentValue, useOnUploadError, useOnUploadStart);

	const [isDragActive, setIsDragActive] = useIsDragActive();
	const intl = useIntl();
	const currentValue = useCurrentValue({ fieldId });
	const filesValue = asFilesValue(currentValue) ?? EMPTY_FILES_VALUE;
	const field = useFormField({ fieldId });
	const attachmentConfiguration = useAttachmentConfiguration();
	const onRemoveFile = useOnRemoveFile(filesValue, fieldId);
	const isInvalid = useFieldInvalid({ fieldId });
	const {
		mediaBrowserConfig,
		mediaClientConfig: mediaClientUploadConfig,
		mediaDropzoneConfig,
	} = attachmentConfiguration?.upload ?? {};
	const { mediaClientConfig: mediaClientReadConfig } = attachmentConfiguration?.read ?? {};
	const fileCollectionName = mediaBrowserConfig?.uploadParams.collection;
	const onUploadEnd = useOnUploadEnd(filesValue, fieldId, fileCollectionName);
	const onUploadError = useOnUploadError(filesValue, fieldId);
	const onUploadStart = useOnUploadStart(filesValue, fieldId, fileCollectionName);

	const onDragEnter = useCallback(() => {
		setIsDragActive(true);
	}, [setIsDragActive]);

	const onDragLeave = useCallback(() => {
		setIsDragActive(false);
	}, [setIsDragActive]);

	const onAttachmentUploadStart = useCallback(
		(payload: UploadsStartEventPayload) => {
			onUploadStart(payload);
			setIsDragActive(false);
		},
		[onUploadStart, setIsDragActive],
	);

	const dropZoneContainer = getDropzone(fieldId);

	const cardActions = useMemo(
		() => [
			{
				handler: onRemoveFile,
				icon: <TrashIcon color="currentColor" spacing="spacious" label="" />,
				label: intl.formatMessage(messages.deleteIconLabel),
			},
		],
		[intl, onRemoveFile],
	);

	if (mediaClientReadConfig === undefined || mediaClientUploadConfig === undefined) {
		return <AttachmentErrorState />;
	}

	return (
		<Box
			xcss={styles.container}
			testId="field-input-attachment-edit-dropzone"
			// eslint-disable-next-line @typescript-eslint/ban-ts-comment
			// @ts-ignore: to unblock React 18.2.0 -> 18.3.1 version bump in Jira
			title={field?.label}
			id={`${fieldId}-dropzone-container`}
			aria-invalid={isInvalid}
			aria-describedby={
				isInvalid && fg('jsm-accessibility-fixes-new-renderer') ? fieldErrorId : undefined
			}
		>
			<Box xcss={styles.dropzoneContainer}>
				<UploadIcon
					color="currentColor"
					spacing="spacious"
					label={intl.formatMessage(messages.uploadIconLabel)}
				/>
				<AttachmentPicker
					mediaClientConfig={mediaClientUploadConfig}
					mediaBrowserConfig={mediaBrowserConfig}
					mediaDropzoneConfig={mediaDropzoneConfig}
					dropzoneContainer={dropZoneContainer}
					onDragEnter={onDragEnter}
					onDragLeave={onDragLeave}
					onUploadError={onUploadError}
					onUploadStart={onAttachmentUploadStart}
					onUploadEnd={onUploadEnd}
				>
					{({ setIsBrowserOpen }) => (
						<FormattedMessage
							{...messages.browseWithDropzone}
							values={{
								a: (content) => (
									// TODO: REQIN-2301 Atlaskit buttons don't have a type that is visually similar to current browse link. Will investigate in follow-up PR.
									// eslint-disable-next-line @atlassian/a11y/anchor-is-valid
									<Link
										href="#"
										onClick={(e) => {
											e.preventDefault();
											setIsBrowserOpen(true);
										}}
									>
										{content}
									</Link>
								),
							}}
						/>
					)}
				</AttachmentPicker>
			</Box>

			{(filesValue.files.length > 0 || isDragActive) && (
				<AttachmentSharedFilmstrip
					actions={cardActions}
					files={filesValue.files}
					mediaClientConfig={mediaClientReadConfig}
					isDragActive={isDragActive}
				/>
			)}
		</Box>
	);
};
